import React, { useCallback, useContext, useEffect, useMemo, useReducer, useRef } from "react";
import { graphql, usePaginationFragment } from "react-relay";

import EventEmitter from "events";
import { throttle } from "lodash";

import AppContext from "~/common/AppContext";
import LoadMoreButton from "~/common/LoadMoreButton";
import { isNotOther, useLazyLoadQuery } from "~/lib/relayUtil";
import type { NodesFromEdges } from "~/lib/utils";
import WebsocketClient from "~/lib/websocket";
import { FeedbackContext } from "~/nav/panels/Feedback";
import EmptyState from "~/nav/panels/Feedback/EmptyState";
import FeedbackCard from "~/nav/panels/Feedback/FeedbackCard";

import type { FeedbackCategory } from "./CategoryDropdown";

import type { FeedbackListPaginationQuery } from "~/__generated__/FeedbackListPaginationQuery.graphql";
import type { FeedbackListQuery, FeedbackListQuery$variables } from "~/__generated__/FeedbackListQuery.graphql";
import type {
	FeedbackListViewer$data as FeedbackListViewer,
	FeedbackListViewer$key,
} from "~/__generated__/FeedbackListViewer.graphql";

import styles from "./FeedbackList.module.css";

export const refetchEmitter = new EventEmitter();

const feedbackListQuery = graphql`
	query FeedbackListQuery(
		$category: FeedbackCategory!
		$feedbackType: FeedbackTypeFilter
		$aboutUserId: ID
		$fromUserId: ID
	) {
		viewer {
			...FeedbackListViewer
				@arguments(category: $category, feedbackType: $feedbackType, aboutUserId: $aboutUserId, fromUserId: $fromUserId)
		}
	}
`;

const feedbackListPaginationQuery = graphql`
	fragment FeedbackListViewer on Viewer
	@argumentDefinitions(
		category: { type: "FeedbackCategory!" }
		feedbackType: { type: "FeedbackTypeFilter" }
		aboutUserId: { type: "ID" }
		fromUserId: { type: "ID" }
		before: { type: "String" }
		after: { type: "String" }
		first: { type: "Int", defaultValue: 25 }
		last: { type: "Int" }
	)
	@refetchable(queryName: "FeedbackListPaginationQuery") {
		feedbacks: feedbackList(
			category: $category
			feedbackType: $feedbackType
			aboutUserId: $aboutUserId
			fromUserId: $fromUserId
			before: $before
			after: $after
			first: $first
			last: $last
		) @connection(key: "FeedbackList_feedbacks") {
			edges {
				node {
					...FeedbackCard @relay(mask: false)
				}
			}
		}
	}
`;

function isNotNull<G>(value: G): value is NonNullable<G> {
	return value != null;
}

function feedbackCompareFn(
	a: NodesFromEdges<FeedbackListViewer["feedbacks"]>,
	b: NodesFromEdges<FeedbackListViewer["feedbacks"]>,
) {
	let bUpdatedAt = new Date(0);
	let aUpdatedAt = new Date(0);

	if (a.__typename === "FeedbackNode" && a.updatedAt) {
		aUpdatedAt = new Date(a.updatedAt);
	} else if (a.__typename === "FeedbackRequestNode" && a.feedback.updatedAt) {
		aUpdatedAt = new Date(a.feedback.updatedAt);
	}

	if (b.__typename === "FeedbackNode" && b.updatedAt) {
		bUpdatedAt = new Date(b.updatedAt);
	} else if (b.__typename === "FeedbackRequestNode" && b.feedback.updatedAt) {
		bUpdatedAt = new Date(b.feedback.updatedAt);
	}

	return +bUpdatedAt - +aUpdatedAt;
}

const AMOUNT_OF_FEEDBACK_PER_FETCH = 25;

interface FeedbackListProps {
	hasAccessToFeedback: boolean;
	hasAccessTo360Feedback: boolean;
	onFeedbackListUpdated?(): void;
}

export default function FeedbackList({
	hasAccessToFeedback,
	hasAccessTo360Feedback,
	onFeedbackListUpdated,
}: FeedbackListProps) {
	const { view, type, about, from } = useContext(FeedbackContext);
	const { user } = useContext(AppContext);
	const listRef = useRef<HTMLDivElement>(null);
	const [, forceUpdate] = useReducer(x => x + 1, 0);

	const props = useLazyLoadQuery<FeedbackListQuery>(
		feedbackListQuery,
		{ category: view, feedbackType: type, aboutUserId: about, fromUserId: from },
		{ fetchPolicy: "store-and-network" },
	);
	const {
		data: viewer,
		refetch,
		hasNext,
		loadNext,
	} = usePaginationFragment<FeedbackListPaginationQuery, FeedbackListViewer$key>(
		feedbackListPaginationQuery,
		props.viewer,
	);

	const feedbackEdges = viewer.feedbacks.edges
		.map(edge => edge.node)
		.filter(isNotNull)
		.filter(isNotOther)
		// Although instances are sorted by the backend: order_by('-updated_at'), this sort is
		// necessary for those cases where we don't refetch results, ex: updating a draft
		.sort(feedbackCompareFn);

	const throttledRefetch = useMemo(
		() =>
			throttle((filters: FeedbackListQuery$variables) => {
				refetch(filters, { fetchPolicy: "store-and-network" });
				onFeedbackListUpdated?.();
			}, 500),
		[refetch, onFeedbackListUpdated],
	);

	const onRefetch = useCallback(() => {
		throttledRefetch({ category: view, feedbackType: type, aboutUserId: about, fromUserId: from });
	}, [throttledRefetch, view, type, about, from]);

	const loadMore = useCallback(
		(callback: () => void) => {
			if (hasNext) {
				loadNext(AMOUNT_OF_FEEDBACK_PER_FETCH, { onComplete: callback });
			}
		},
		[hasNext, loadNext],
	);

	useEffect(() => {
		if (!user) {
			return;
		}
		const key = `feedback.${user.id}`.replace(/=/g, "");
		const socket = WebsocketClient.getInstance().getOrJoinGroup(key, {});
		socket.on("message", onRefetch);
		if (listRef.current) {
			forceUpdate();
		}

		return () => {
			socket.off("message", onRefetch);
		};
	}, [user, onRefetch, listRef]);

	const onEmitterRefetch = useCallback(
		(category: FeedbackCategory) => {
			if (category === view) {
				// Emitter decides when to refetch depending on where the new item should go
				refetch(
					{ category: view, feedbackType: type, aboutUserId: about, fromUserId: from },
					{ fetchPolicy: "store-and-network" },
				);
			}
		},
		[refetch, view, type, about, from],
	);

	useEffect(() => {
		refetchEmitter.on("FeedbackListRefetch", onEmitterRefetch);
		return () => {
			refetchEmitter.removeListener("FeedbackListRefetch", onEmitterRefetch);
		};
	}, [onRefetch, onEmitterRefetch]);

	return (
		<>
			{feedbackEdges.length > 0 ? (
				<div className={styles.list} ref={listRef}>
					{feedbackEdges.map(feedback => (
						<FeedbackCard key={feedback.id} feedback={feedback} refetch={onRefetch} />
					))}
					{listRef.current && hasNext && (
						<div className={styles.loadMoreContainer}>
							<LoadMoreButton
								dotted={false}
								fireOnMount={true}
								disabled={!hasNext}
								loadMore={loadMore}
								context={listRef.current}
								debounceTimeout={50}
							/>
						</div>
					)}
				</div>
			) : (
				<EmptyState hasAccessToFeedback={hasAccessToFeedback} hasAccessTo360Feedback={hasAccessTo360Feedback} />
			)}
		</>
	);
}
